/**
 * TanStack Query hooks for the API-key admin endpoints (Phase 9.10).
 *
 * Wraps:
 *   GET    /api/v1/admin/api-keys           -> useApiKeys
 *   POST   /api/v1/admin/api-keys           -> useCreateApiKey
 *   DELETE /api/v1/admin/api-keys/{id}      -> useRevokeApiKey
 *
 * Create returns the cleartext `raw_key` exactly once - the caller is
 * expected to capture the response value, surface it via the show-once
 * modal, and never persist it.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import api from "@/lib/api";
import type {
  ApiKeyCreateRequest,
  ApiKeyCreateResponse,
  ApiKeyResponse,
} from "@/types/api";

export const API_KEYS_QUERY_KEY = ["admin", "api-keys"] as const;

export interface UseApiKeysArgs {
  /** Pass `true` to fetch revoked keys as well. Defaults to `false`. */
  includeRevoked?: boolean;
}

export function useApiKeys(
  args: UseApiKeysArgs = {},
): UseQueryResult<ApiKeyResponse[]> {
  const { includeRevoked = false } = args;
  return useQuery({
    queryKey: [...API_KEYS_QUERY_KEY, { includeRevoked }],
    queryFn: async () => {
      const response = await api.get<ApiKeyResponse[]>("/admin/api-keys", {
        params: includeRevoked ? { include_revoked: true } : undefined,
      });
      return response.data;
    },
  });
}

export function useCreateApiKey(): UseMutationResult<
  ApiKeyCreateResponse,
  Error,
  ApiKeyCreateRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: ApiKeyCreateRequest) => {
      const response = await api.post<ApiKeyCreateResponse>(
        "/admin/api-keys",
        body,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: API_KEYS_QUERY_KEY });
    },
  });
}

export function useRevokeApiKey(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (keyId: string) => {
      await api.delete(`/admin/api-keys/${keyId}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: API_KEYS_QUERY_KEY });
    },
  });
}
